import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Mail, Phone } from "lucide-react";
import Logo from "@/components/site/Logo";
import { BRAND, NAV, SERVICE_AREA } from "@/lib/site";

// If a footer link points to the page the user is already on, scroll to the
// top smoothly so the click always produces visible movement. Cross-page
// navigation is handled by <ScrollManager /> in App.js.
const samePageScrollTop = (to) => (e) => {
  if (typeof window !== "undefined" && window.location.pathname === to) {
    e.preventDefault();
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }
};

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative overflow-hidden bg-black text-white"
    >
      <div className="pointer-events-none absolute -right-40 -top-40 h-[560px] w-[560px] rounded-full bg-[#4F0DFF]/25 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-40 -left-40 h-[500px] w-[500px] rounded-full bg-[#0AB5FF]/20 blur-[120px]" />

      <div className="container-lw relative py-16 md:py-24">
        {/* CTA row */}
        <div className="grid gap-10 border-b border-white/10 pb-14 md:grid-cols-[1.4fr_1fr] md:pb-16">
          <div>
            <div className="eyebrow text-[#0AB5FF]">Bring the standard to your lot</div>
            <h2 className="mt-4 h-display text-4xl md:text-5xl lg:text-6xl">
              The standard should not depend on
              <br className="hidden md:block" />
              <span className="text-gradient-lw"> whoever has time.</span>
            </h2>
            <p className="mt-5 max-w-xl text-white/70">
              Put the inventory on a system. LotWise Auto handles photos, details,
              formatting, and updates so every unit is complete, current, and
              properly presented.
            </p>
          </div>
          <div className="flex flex-col items-start justify-end gap-3 md:items-end">
            <Link
              to="/request-a-visit"
              onClick={samePageScrollTop("/request-a-visit")}
              data-testid="footer-request-visit-cta"
              className="btn-primary-lw"
            >
              Request a Visit
              <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link
              to="/sample-listings"
              onClick={samePageScrollTop("/sample-listings")}
              data-testid="footer-sample-listings-cta"
              className="btn-ghost-lw-dark"
            >
              View Sample Listings
            </Link>
          </div>
        </div>

        {/* Info grid */}
        <div className="grid gap-10 py-14 md:grid-cols-4">
          <div className="md:col-span-1">
            <Logo variant="light" />
            <p className="mt-5 max-w-xs text-sm text-white/60">
              Operational infrastructure for dealerships. Handled as a system.
              Presented properly.
            </p>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-white/40">
              Site
            </div>
            <ul className="mt-4 space-y-2.5">
              {NAV.map((n) => (
                <li key={n.to}>
                  <Link
                    to={n.to}
                    onClick={samePageScrollTop(n.to)}
                    data-testid={`footer-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-sm text-white/70 transition hover:text-white"
                  >
                    {n.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/request-a-visit"
                  onClick={samePageScrollTop("/request-a-visit")}
                  data-testid="footer-link-request-visit"
                  className="text-sm text-white/70 transition hover:text-white"
                >
                  Request a Visit
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-white/40">
              Service Area
            </div>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <Link
                  to="/dealerships/ottawa"
                  data-testid="footer-region-ottawa"
                  className="text-white/75 transition hover:text-white"
                >
                  Ottawa dealerships
                </Link>
              </li>
              <li>
                <Link
                  to="/dealerships/kingston"
                  data-testid="footer-region-kingston"
                  className="text-white/75 transition hover:text-white"
                >
                  Kingston dealerships
                </Link>
              </li>
              <li>
                <Link
                  to="/dealerships/pembroke"
                  data-testid="footer-region-pembroke"
                  className="text-white/75 transition hover:text-white"
                >
                  Pembroke &amp; Ottawa Valley
                </Link>
              </li>
              <li>
                <Link
                  to="/dealerships/cornwall"
                  data-testid="footer-region-cornwall"
                  className="text-white/75 transition hover:text-white"
                >
                  Cornwall &amp; SDG
                </Link>
              </li>
              <li>
                <Link
                  to="/dealerships/perth"
                  data-testid="footer-region-perth"
                  className="text-white/75 transition hover:text-white"
                >
                  Perth &amp; Lanark
                </Link>
              </li>
              <li>
                <Link
                  to="/dealer-brief"
                  data-testid="footer-dealer-brief"
                  className="text-white/75 transition hover:text-white"
                >
                  Dealer Brief (PDF)
                </Link>
              </li>
            </ul>
            <div className="mt-4 flex flex-wrap gap-1.5 text-xs">
              {SERVICE_AREA.primary.slice(0, 6).map((city) => (
                <span
                  key={city}
                  className="inline-flex items-center rounded-full border border-white/15 bg-white/[0.02] px-2 py-0.5 text-white/55"
                >
                  {city}
                </span>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-white/50">
              <MapPin className="h-3.5 w-3.5" /> Priority region:{" "}
              {BRAND.region}
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-white/40">
              Contact
            </div>
            <ul className="mt-4 space-y-3 text-sm">
              <li>
                <a
                  href={`mailto:${BRAND.email}`}
                  data-testid="footer-email"
                  className="inline-flex items-center gap-2 text-white/80 transition hover:text-white"
                >
                  <Mail className="h-4 w-4" /> {BRAND.email}
                </a>
              </li>
              <li>
                <a
                  href={BRAND.phoneHref}
                  data-testid="footer-phone"
                  className="inline-flex items-center gap-2 text-white/80 transition hover:text-white"
                >
                  <Phone className="h-4 w-4" /> {BRAND.phone}
                </a>
              </li>
            </ul>
            <p className="mt-4 text-xs text-white/50">
              Currently serving {BRAND.region}.<br />
              Accepting waitlist applications from other regions.
            </p>
          </div>
        </div>

        <div className="flex flex-col items-start justify-between gap-3 border-t border-white/10 pt-8 md:flex-row md:items-center">
          <p className="text-xs text-white/50">
            © {new Date().getFullYear()} {BRAND.name}. All rights reserved.
          </p>
          <p className="text-xs text-white/50" data-testid="footer-kostaja-credit">
            Web design by{" "}
            <a
              href="https://kostaja.ca"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-white/80 underline decoration-white/30 underline-offset-4 transition hover:text-white hover:decoration-white"
            >
              Kostaja
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
